import { JSX } from "react";
import { WafflesFilterType } from "./filterExports";

interface ColumnGroup<T> {
  alignment?: TextAlignment;
  children: Array<keyof T>;
  hideKey?: boolean;
  key: string;
  showInfoIcon?: boolean;
  testId: string;
  tooltip?: string;
}

interface ColumnConfig {
  [key: string]: WafflesColumn;
}

enum SortDirections {
  Asc = "ascending",
  Desc = "descending",
  None = "none",
}

enum TextAlignment {
  Left = "left",
  Center = "center",
  Right = "right",
  // TypeScript won't take Justified as an option
}

const WafflesClassNames = {
  "headRow": "waffles-head-row",
  "table": "waffles-table",
  "tbody": "",
  "td": "waffles-cell",
  "th": "waffles-head",
  "thead": "",
  "tr": "waffles-row",
}

// named WafflesColumn to avoid confusion with react-virtualized Column in VS
interface WafflesColumn {
  alignment?: TextAlignment;
  cargoTitle?: string;
  displayElement?: JSX.Element;
  displayText?: string;
  filterText?: string;
  filterType?: WafflesFilterType;
  handleSpecialSort?: (col: string) => void;
  hidden?: boolean;
  maxWidth?: string;
  minWidth?: string; // This property applies the min-width css property to the <div> that wraps the content within the column's table body cells.
  noFilter?: boolean;
  noHorizontalPadding?: boolean;
  noSort?: boolean;
  noResize?: boolean;
  paddingLeft?: string;
  sizeContentToFit?: boolean;
  // I like using multiples of ten for this value, so there's some wiggle room if things ever need to be rearranged
  // also worth noting that if you don't use this property, Waffles defaults to the order of keys in its data prop
  // but any columns that have a sort order will be placed before any that don't, so it's kind of one or the other
  sortOrder?: number;
  specialSort?: JSX.Element;
  sticky?: boolean;
  style?: React.CSSProperties; // This property applies the CSS fields to the table header <th> cell directly.
  testId: string;
  tooltip?: string | JSX.Element;
  wrapContent?: boolean;
}

const wafflesKeyColumnName = "key";

const numberSort = <T>(
  data: T[],
  columnName: keyof T,
  multiplier: number,
  // this optional function parameter basically serves as an explicit type converter, or it can be used to get the right value if item is an object
  numberGenerator: (item: unknown) => string = (item: unknown) => item as string,
): T[] => {
  const nans = new Array<T>();
  let ns = new Array<T>();
  data.forEach((item) => {
    if (isNaN(parseNumber(numberGenerator(item[columnName])))) {
      nans.push(item);
    } else {
      ns.push(item);
    }
  });
  ns = ns.sort((a, b) => {
    const fa = parseNumber(numberGenerator(a[columnName]));
    const fb = parseNumber(numberGenerator(b[columnName]));
    return (fa > fb ? 1 : -1) * multiplier;
  });
  return multiplier === 1 ? nans.concat(ns) : ns.concat(nans);
};

//this cleans the string of commas and other locale specific symbols so the string can be parsed
const parseNumber = (value: string, locale = navigator.language): number => {
  if (value === undefined || value === null) {
    return -Infinity;
  }
  if (typeof value === "number") {
    return value;
  }
  const example = Intl.NumberFormat(locale).format(1.1);
  const cleanPattern = new RegExp(`[^-+0-9${example.charAt(1)}]`, "g");
  const cleaned = value.replace(cleanPattern, "");
  const normalized = cleaned.replace(example.charAt(1), ".");

  return parseFloat(normalized);
};

const printNumberAsString = (input: number, fallback: string): string => {
  // We can't do a falsey check because 0 would fail, and we can't just do !isNaN because null returns false for isNaN
  if (input !== undefined && input !== null) {
    return input.toLocaleString();
  }
  return fallback;
};

interface SortingMetadata<T> {
  sortedColumn: keyof T;
  sortDirection: SortDirections;
}

interface Virtualization {
  rowHeight: number;
  viewportHeight: number;
}

interface WafflesPaginationProps {
  changePage: (pageNumber: number) => void;
  changeRecordsPerPage?: (pageNumber: number, recordsPerPage: number) => void;
  classNames?: string;
  enableBackendPagination?: boolean;
  handlePageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  recordsPerPage: number;
  resetToFirstPage?: boolean;
  showPageSizeSelector?: boolean;
  skip?: number;
  totalRecords: number;
}

interface PaginationResult<T> {
  totalCount: number,
  items: T[]
}

enum WafflesThemes {
  Default = "default",
  // used in servicing provider flyout
  Flyout = "flyout",
  // used on dashboards
  Simple = "simple",
  // used to make body of pci dashboards scrollable
  Scrollable = "scrollable",
}

enum WafflesExpansionToggleStatus {
  Closed,
  Loading,
  Open,
}

export {
  ColumnGroup,
  ColumnConfig,
  numberSort,
  PaginationResult,
  parseNumber,
  printNumberAsString,
  SortingMetadata,
  SortDirections,
  TextAlignment,
  Virtualization,
  WafflesClassNames,
  WafflesColumn,
  wafflesKeyColumnName,
  WafflesPaginationProps,
  WafflesExpansionToggleStatus,
  WafflesThemes,
};
